const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');

function initializeMobileMenu() {
  const panel = document.querySelector('.mobile-menu-panel');
  const toggle = document.querySelector('.mobile-menu-toggle');

  if (!panel || !toggle || toggle.dataset.mobileMenuReady === 'true') return;

  toggle.dataset.mobileMenuReady = 'true';

  let isOpen = false;
  let savedScrollY = 0;
  let savedBodyStyles = null;
  let activeSubmenu = null;

  const primaryLevel = panel.querySelector('[data-mobile-menu-level="primary"]');
  const submenuLevels = [...panel.querySelectorAll('[data-mobile-menu-level="submenu"]')];

  const getFocusableElements = () => {
    const scope = activeSubmenu || primaryLevel || panel;
    return [
      toggle,
      ...scope.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
    ].filter((element) => !element.hasAttribute('inert') && element.offsetParent !== null);
  };

  const resetSubmenuState = () => {
    panel.classList.remove('is-submenu-open');
    activeSubmenu = null;

    panel.querySelectorAll('[data-mobile-menu-open]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    submenuLevels.forEach((submenu) => {
      submenu.classList.remove('is-active');
      submenu.inert = true;
      submenu.setAttribute('aria-hidden', 'true');
      submenu.removeAttribute('hidden');
    });

    if (primaryLevel) {
      primaryLevel.inert = false;
      primaryLevel.setAttribute('aria-hidden', 'false');
      primaryLevel.removeAttribute('hidden');
    }
  };

  const openSubmenu = (handle) => {
    const submenu = panel.querySelector(`[data-mobile-menu-submenu="${handle}"]`);
    const trigger = panel.querySelector(`[data-mobile-menu-open="${handle}"]`);
    if (!submenu || !trigger) return;

    submenuLevels.forEach((level) => {
      const isActive = level === submenu;
      level.classList.toggle('is-active', isActive);
      level.inert = !isActive;
      level.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    if (primaryLevel) {
      primaryLevel.setAttribute('aria-hidden', 'true');
    }

    panel.querySelectorAll('[data-mobile-menu-open]').forEach((button) => {
      button.setAttribute('aria-expanded', button === trigger ? 'true' : 'false');
    });

    activeSubmenu = submenu;
    panel.classList.add('is-submenu-open');

    requestAnimationFrame(() => {
      const firstLink = submenu.querySelector('.mobile-menu-panel__link');
      firstLink?.focus({ preventScroll: true });
    });
  };

  const closeSubmenu = ({ restoreFocus = true } = {}) => {
    if (!activeSubmenu) return;

    const handle = activeSubmenu.getAttribute('data-mobile-menu-submenu');
    const trigger = handle
      ? panel.querySelector(`[data-mobile-menu-open="${handle}"]`)
      : null;

    panel.classList.remove('is-submenu-open');

    submenuLevels.forEach((level) => {
      level.classList.remove('is-active');
      level.inert = true;
      level.setAttribute('aria-hidden', 'true');
    });

    if (primaryLevel) {
      primaryLevel.inert = false;
      primaryLevel.setAttribute('aria-hidden', 'false');
    }

    panel.querySelectorAll('[data-mobile-menu-open]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    activeSubmenu = null;

    if (restoreFocus) {
      requestAnimationFrame(() => {
        trigger?.focus({ preventScroll: true });
      });
    }
  };

  const lockScroll = () => {
    savedScrollY = window.scrollY;
    savedBodyStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      right: document.body.style.right,
      left: document.body.style.left,
      width: document.body.style.width,
      paddingRight: document.body.style.paddingRight,
    };

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.documentElement.classList.add('mobile-menu-open');
    document.body.classList.add('mobile-menu-open');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.right = '0';
    document.body.style.left = '0';
    document.body.style.width = '100%';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  };

  const unlockScroll = () => {
    document.documentElement.classList.remove('mobile-menu-open');
    document.body.classList.remove('mobile-menu-open');

    if (savedBodyStyles) {
      Object.assign(document.body.style, savedBodyStyles);
      savedBodyStyles = null;
    }

    window.scrollTo(0, savedScrollY);
  };

  const finishClose = () => {
    if (isOpen) return;
    resetSubmenuState();
    panel.classList.remove('is-closing', 'is-animating');
    panel.classList.add('is-closed');
    panel.inert = true;
    unlockScroll();
    toggle.focus({ preventScroll: true });
  };

  const openMenu = () => {
    if (!MOBILE_QUERY.matches || isOpen) return;

    isOpen = true;
    resetSubmenuState();
    lockScroll();
    panel.inert = false;
    panel.classList.remove('is-closing', 'is-closed');
    panel.classList.add('is-animating');
    panel.setAttribute('aria-hidden', 'false');
    panel.classList.add('is-open');
    toggle.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Close menu');
  };

  const closeMenu = ({ restoreFocus = true } = {}) => {
    if (!isOpen) return;

    isOpen = false;
    resetSubmenuState();
    panel.classList.add('is-closing', 'is-animating');
    panel.classList.remove('is-open');
    toggle.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open menu');
    panel.setAttribute('aria-hidden', 'true');

    if (!restoreFocus) toggle.dataset.skipFocusRestore = 'true';
  };

  toggle.addEventListener('click', () => {
    if (isOpen) closeMenu();
    else openMenu();
  });

  panel.addEventListener('transitionend', (event) => {
    if (event.target !== panel || event.propertyName !== 'transform') return;

    if (isOpen) {
      panel.classList.remove('is-animating');
      const firstLink = primaryLevel?.querySelector('.mobile-menu-panel__link');
      firstLink?.focus({ preventScroll: true });
      return;
    }

    if (toggle.dataset.skipFocusRestore === 'true') {
      delete toggle.dataset.skipFocusRestore;
      resetSubmenuState();
      panel.classList.remove('is-closing', 'is-animating');
      panel.classList.add('is-closed');
      panel.inert = true;
      unlockScroll();
      return;
    }

    finishClose();
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen || !MOBILE_QUERY.matches) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      if (activeSubmenu) closeSubmenu();
      else closeMenu();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements();
    if (focusable.length === 0) return;

    const currentIndex = focusable.indexOf(document.activeElement);
    const nextIndex = event.shiftKey
      ? (currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1)
      : (currentIndex === -1 || currentIndex === focusable.length - 1 ? 0 : currentIndex + 1);

    event.preventDefault();
    focusable[nextIndex].focus();
  });

  panel.addEventListener('click', (event) => {
    const openTrigger = event.target.closest('[data-mobile-menu-open]');
    if (openTrigger) {
      event.preventDefault();
      openSubmenu(openTrigger.getAttribute('data-mobile-menu-open'));
      return;
    }

    const backTrigger = event.target.closest('[data-mobile-menu-back]');
    if (backTrigger) {
      event.preventDefault();
      closeSubmenu();
      return;
    }

    const link = event.target.closest('a[href]');
    if (!link) return;
    closeMenu({ restoreFocus: false });
  });

  window.addEventListener('pageshow', () => {
    if (!isOpen) {
      resetSubmenuState();
      return;
    }
    closeMenu({ restoreFocus: false });
  });

  MOBILE_QUERY.addEventListener('change', (event) => {
    if (!event.matches) {
      if (isOpen) {
        isOpen = false;
        panel.classList.remove('is-open', 'is-closing', 'is-animating');
        panel.classList.add('is-closed');
        panel.setAttribute('aria-hidden', 'true');
        panel.inert = true;
        toggle.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Open menu');
        unlockScroll();
      }
      resetSubmenuState();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMobileMenu, { once: true });
} else {
  initializeMobileMenu();
}
